import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserStatus } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Text02 } from 'components/styles';
import { Translate, TranslationKey } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { PermissionMap } from '../use-permission';
import { TeamMembersMoreActionsMenu } from './more-actions-menu/more-actions-menu';
import { MemberCellRendererParams } from './table-utils';

const SecondaryText = styled.span`
  ${Text02};
`;

const ClickableName = styled.div`
  cursor: pointer;
`;
const DeactivatedText = styled.span`
  color: var(--red-60);
`;

const InvitedText = styled.span`
  color: var(--blue-60);
`;

interface MemberCellParamsWithTranslate extends MemberCellRendererParams {
  translate: Translate;
}

export const getAvatar = ({ data }: MemberCellRendererParams): string => {
  return renderToStaticMarkup(
    <Avatar
      name={fullName(data.user)}
      src={data.user?.photoUrl}
      size="large"
    />,
  );
};

export const getPermission = ({
  data,
  translate,
}: MemberCellParamsWithTranslate): string => {
  if (
    !data.permissionSet ||
    !PermissionMap[data.permissionSet?.permissionSetType]
  ) {
    return '';
  }

  const permission = translate(
    `permissions.${
      PermissionMap[data.permissionSet?.permissionSetType]
    }` as TranslationKey,
  );

  return renderToStaticMarkup(<SecondaryText>{permission}</SecondaryText>);
};

export const TeamMemberOptionsRenderer = ({
  showMemberDetail,
  data,
  openCostRateManager,
}) => {
  return (
    <TeamMembersMoreActionsMenu
      member={data}
      showMemberDetail={showMemberDetail}
      openCostRateManager={openCostRateManager}
    />
  );
};

export const getUserName = ({
  data,
  translate,
}: MemberCellParamsWithTranslate): string => {
  const status =
    data.user.userStatus === UserStatus.Inactive
      ? translate('deactivated')
      : translate('invited');

  return renderToStaticMarkup(
    <ClickableName data-cy={data.user.userId}>
      {fullName(data.user)}
      <br />
      {data.user.userStatus === UserStatus.Inactive && (
        <DeactivatedText>{status}</DeactivatedText>
      )}
      {data.user.userStatus === UserStatus.Invited && (
        <InvitedText>{status}</InvitedText>
      )}
    </ClickableName>,
  );
};

export const NoResultTeamMemeberEmptyState: FC = () => {
  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faTelescope} />}
        title={
          <FormattedMessage id="settings.team_members.empty.state.title" />
        }
        message={
          <FormattedMessage id="settings.team_members.empty.state.message" />
        }
        secondaryCTA={null}
      />
    </EmptyStateContainer>
  );
};
